import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { IonPopover, ModalController } from '@ionic/angular/standalone';
import { ChipComponent } from 'src/app/components/chip/chip.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { ItemValueComponent } from 'src/app/components/item-value/item-value.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { TeamSelectorModal } from 'src/app/modals/team-selector/team-selector.modal';
import { ServiceNamePipe } from 'src/app/pipes/service-name.pipe';
import { Team } from 'src/app/types/aggregate-graph.types';

type ServicePickerValue = { id: string, name: string, shortName: string } | null;

@Component({
	selector: 'app-service-picker',
	templateUrl: './service-picker.control.html',
	styleUrl: './service-picker.control.scss',
	imports: [ReactiveFormsModule, ItemComponent, ItemLabelComponent, ChipComponent, ServiceNamePipe, ItemValueComponent, IonPopover],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: ServicePickerControl,
		multi: true
	}]
})
export class ServicePickerControl implements ControlValueAccessor {

	@Input() readOnly: boolean = false;
	@Input() label: string = '';
	@Input() placeholder: string = '';

	value: ServicePickerValue = null;

	get placeholderValue() { return this.readOnly ? '' : this.placeholder }

	@ViewChild('popover') popover!: IonPopover;
	popoverIsOpen: boolean = false;

	constructor(private changeDetectorRef: ChangeDetectorRef, private modalController: ModalController) {
	}

	writeValue(value: ServicePickerValue): void {
		this.value = value;

		// this is required for the view to update correctly (it's a general approach recommended by Angular devs using ChangeDetectionStrategy.OnPush: https://github.com/angular/angular/issues/21780)
		this.changeDetectorRef.markForCheck();
	}

	private onChange: (value: ServicePickerValue) => void = () => { };

	registerOnChange(fn: (value: ServicePickerValue) => void): void {
		this.onChange = fn;
	}

	private onTouched: () => void = () => { };

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
	}

	async controlClicked(event: Event) {
		if (this.readOnly) return;

		this.onTouched();

		if (this.value) {
			event.stopPropagation();
			this.popover.event = event;
			this.popoverIsOpen = true;
		}
		else {
			await this.presentServiceSelectorModal();
		}
	}

	async changeService() {
		this.popoverIsOpen = false;
		await this.presentServiceSelectorModal();
	}

	clearService() {
		this.popoverIsOpen = false;
		this.value = null;
		this.changeValue();
	}

	private async presentServiceSelectorModal() {
		const popover = await this.modalController.create({
			component: TeamSelectorModal,
			cssClass: 'stacked-modal',
			backdropDismiss: false
		});

		await popover.present();

		const { data } = await popover.onWillDismiss();

		if (!data) return;

		const team: Team = data.team;

		if (team.id === this.value?.id && team.name === this.value?.name && team.shortName === this.value?.shortName) return; // name can get changed for a team

		this.value = { id: team.id, name: team.name, shortName: team.shortName };
		this.changeValue();
	}

	private changeValue() {
		this.changeDetectorRef.markForCheck(); // ensure the view shows the new value (needed due to OnPush change detection strategy)
		this.onChange(this.value);
	}
}